import type {
  BalanceResponseBody,
  ListApiKeysResponse,
  PublicStatusResponse,
  PublicStatusState,
  UsageByKeyResponse,
  UsageDailyResponse,
  UsageHourlyBucket,
  UsageHourlyResponse,
  UsageSummaryResponse,
} from '@nadoutong/contracts';
import type { UseQueryResult } from '@tanstack/react-query';
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useBalance, useDisplayRate } from '../api/useBilling.ts';
import { useKeysList } from '../api/useKeys.ts';
import { useNadoutongPublicStatus } from '../api/useStatus.ts';
import { useUsageByKey, useUsageDaily, useUsageHourly, useUsageSummary } from '../api/useUsage.ts';
import { Line } from '../charts/index.ts';
import { GiftBalanceLine } from '../components/GiftBalanceLine.tsx';
import {
  formatDisplayRateHint,
  formatTimestamp,
  formatTokens,
  formatUsdMicrosOrCentsAsCnyDisplay,
  formatUsdMicrosOrCentsAsCnyMetered,
  usdMicrosFromCentsFallback,
} from '../components/formatters.ts';
import { hasPermission, useSession } from '../hooks/useSession.tsx';
import { useT } from '../i18n/index.ts';
import type { I18nKey } from '../i18n/index.ts';
import {
  Button,
  Chip,
  EmptyState,
  KpiCard,
  PageHeader,
  Panel,
  Skeleton,
  StatusLamp,
  TBody,
  Table,
  TableSkeleton,
  Td,
  Th,
  THead,
  Tr,
  buttonClass,
  cx,
} from '../ui/index.ts';
import type { ChipTone, LampTone } from '../ui/index.ts';

/**
 * Dashboard — the tenant ACTION homepage (epic #897 PR 4).
 *
 * Deliberately not an analytics surface: deep analysis lives at
 * /usage. This page answers four glance-pace questions — do I have
 * money, is the service up, what do I do next, did anything happen
 * recently — and links out for everything deeper. The former
 * `admin-dashboard` deep-analysis query (stacked charts / health
 * meters / budget risk) was removed here to kill the Dashboard↔Usage
 * duplication (epic #897 AC 6).
 *
 * Every panel loads and fails independently: its own Skeleton while
 * pending, an inline message + retry button on error (no toast for
 * load failures), and the rest of the page stays useful.
 *
 * The "last request" KPI is derived from the most recent non-empty
 * hourly bucket — platform-api has no direct "last request" timestamp
 * and duplicating usage accounting for one is not worth it; the copy
 * labels the value as approximate (「约」).
 */

type TranslationFn = ReturnType<typeof useT>;

const OVERALL_LAMP_TONE: Record<PublicStatusState, LampTone> = {
  healthy: 'ok',
  busy: 'warn',
  unavailable: 'bad',
};

const VENDOR_CHIP_TONE: Record<PublicStatusState, ChipTone> = {
  healthy: 'ok',
  busy: 'warn',
  unavailable: 'bad',
};

function overallStateText(state: PublicStatusState, t: TranslationFn): string {
  switch (state) {
    case 'healthy':
      return t('dashboard.status.overall.healthy');
    case 'busy':
      return t('dashboard.status.overall.busy');
    case 'unavailable':
      return t('dashboard.status.overall.unavailable');
  }
}

function firstOfMonthIso(): string {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0).toISOString();
}

function nextMonthStartIso(): string {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth() + 1, 1, 0, 0, 0, 0).toISOString();
}

/** `YYYY-MM-DD HH:mm` → `MM-DD HH:mm` — stat cards have a width budget. */
function shortTimestamp(iso: string): string {
  const full = formatTimestamp(iso);
  return /^\d{4}-/.test(full) ? full.slice(5) : full;
}

function lastNonEmptyBucket(buckets: readonly UsageHourlyBucket[]): UsageHourlyBucket | null {
  return (
    [...buckets]
      .filter((bucket) => bucket.requests > 0)
      .sort((a, b) => b.hour.localeCompare(a.hour))[0] ?? null
  );
}

function rollupRequestsByDay(
  buckets: readonly UsageHourlyBucket[],
): readonly { readonly date: string; readonly requests: number }[] {
  const byDay = new Map<string, number>();
  for (const bucket of buckets) {
    const day = bucket.hour.slice(0, 10);
    byDay.set(day, (byDay.get(day) ?? 0) + bucket.requests);
  }
  return [...byDay.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([date, requests]) => ({ date, requests }));
}

export function Dashboard(): JSX.Element {
  const t = useT();
  const { user } = useSession();
  const canReadBilling = hasPermission(user, 'org:billing:read');
  const canManageBilling = hasPermission(user, 'org:billing:manage');
  const canReadOrgUsage = hasPermission(user, 'org:usage:read');

  const monthRange = useMemo(() => ({ from: firstOfMonthIso(), to: nextMonthStartIso() }), []);

  const balanceQ = useBalance({ enabled: canReadBilling });
  const displayRateQ = useDisplayRate();
  const usageQ = useUsageSummary(monthRange);
  const keysQ = useKeysList();
  const hourlyQ = useUsageHourly(7);
  const dailyQ = useUsageDaily({ days: 7 });
  const byKeyQ = useUsageByKey(monthRange);
  const statusQ = useNadoutongPublicStatus();

  const cnyPerUsd = displayRateQ.data?.cnyPerUsd ?? null;

  return (
    <div data-testid="dashboard-page" className="flex flex-col gap-4">
      <PageHeader
        title={t('dashboard.title')}
        sub={user !== null ? t('dashboard.header.sub', { email: user.email }) : undefined}
        testId="dashboard-header"
      />

      <div
        data-testid="dashboard-kpi-grid"
        className={cx(
          'grid grid-cols-1 gap-4',
          canReadBilling
            ? 'lg:grid-cols-2 xl:grid-cols-[minmax(0,1.4fr)_repeat(3,minmax(0,1fr))]'
            : 'sm:grid-cols-2 xl:grid-cols-3',
        )}
      >
        {canReadBilling ? (
          <BalanceHero
            balanceQ={balanceQ}
            cnyPerUsd={cnyPerUsd}
            canManageBilling={canManageBilling}
          />
        ) : null}
        <MonthSpendKpi usageQ={usageQ} cnyPerUsd={cnyPerUsd} />
        <ActiveKeysKpi keysQ={keysQ} />
        <LastRequestKpi hourlyQ={hourlyQ} />
      </div>

      <StatusStrip statusQ={statusQ} />

      <QuickActions canManageBilling={canManageBilling} />

      {canReadOrgUsage ? (
        <TrendRow hourlyQ={hourlyQ} dailyQ={dailyQ} cnyPerUsd={cnyPerUsd} />
      ) : null}

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <RecentActivityPanel hourlyQ={hourlyQ} cnyPerUsd={cnyPerUsd} />
        <TopKeysPanel byKeyQ={byKeyQ} cnyPerUsd={cnyPerUsd} />
      </div>
    </div>
  );
}

/**
 * Inline per-panel failure: message + retry, no toast (DESIGN.md §7 —
 * async load failures never double-report).
 */
function PanelRetry(props: {
  readonly message: string;
  readonly onRetry: () => void;
  readonly testId?: string;
}): JSX.Element {
  const t = useT();
  return (
    <div role="alert" data-testid={props.testId} className="flex flex-wrap items-center gap-3">
      <p className="m-0 text-dense text-bad">{props.message}</p>
      <Button variant="ghost" size="sm" onClick={props.onRetry}>
        {t('dashboard.retry')}
      </Button>
    </div>
  );
}

function RetryInline(props: { readonly label: string; readonly onRetry: () => void }): JSX.Element {
  const t = useT();
  return (
    <span className="inline-flex items-center gap-2">
      <span>{props.label}</span>
      <Button variant="ghost" size="sm" onClick={props.onRetry}>
        {t('dashboard.retry')}
      </Button>
    </span>
  );
}

function BalanceHero(props: {
  readonly balanceQ: UseQueryResult<BalanceResponseBody>;
  readonly cnyPerUsd: number | null;
  readonly canManageBilling: boolean;
}): JSX.Element {
  const t = useT();
  const balance = props.balanceQ.data;
  const funded =
    balance !== undefined &&
    usdMicrosFromCentsFallback(balance.balanceUsdMicros, balance.balanceUsdCents) > 0;
  const rateHint = formatDisplayRateHint(props.cnyPerUsd);
  return (
    <Panel
      caption={t('dashboard.hero.caption')}
      padded
      testId="dashboard-hero-balance"
      className="lg:col-span-2 xl:col-span-1"
      actions={
        balance !== undefined ? (
          <Chip tone={funded ? 'ok' : 'warn'} testId="dashboard-hero-chip">
            {funded ? t('dashboard.hero.funded') : t('dashboard.hero.zero')}
          </Chip>
        ) : undefined
      }
    >
      {props.balanceQ.isLoading ? (
        <Skeleton className="h-24" testId="dashboard-hero-loading" />
      ) : props.balanceQ.isError ? (
        <PanelRetry
          message={t('dashboard.hero.error')}
          onRetry={() => {
            void props.balanceQ.refetch();
          }}
          testId="dashboard-hero-error"
        />
      ) : balance !== undefined ? (
        <div className="flex flex-col gap-3">
          <span className="font-mono text-stat-lg text-paper" data-testid="dashboard-hero-value">
            {formatUsdMicrosOrCentsAsCnyDisplay(
              balance.balanceUsdMicros,
              balance.balanceUsdCents,
              props.cnyPerUsd,
            )}
          </span>
          <GiftBalanceLine
            balance={balance}
            cnyPerUsd={props.cnyPerUsd}
            testId="dashboard-hero-gift"
          />
          <p className="m-0 font-mono text-micro text-paper-low">
            {t('dashboard.hero.updated', { timestamp: formatTimestamp(balance.updatedAt) })}
            {rateHint === '—' ? '' : ` · ${rateHint}`}
          </p>
          {props.canManageBilling ? (
            <Link
              to="/billing/topup"
              className={cx(buttonClass('primary'), 'w-fit')}
              data-testid="dashboard-hero-topup"
            >
              {t('dashboard.hero.topup')}
            </Link>
          ) : funded ? null : (
            <p className="m-0 text-dense text-paper-low">{t('dashboard.hero.zero_readonly')}</p>
          )}
        </div>
      ) : null}
    </Panel>
  );
}

function MonthSpendKpi(props: {
  readonly usageQ: UseQueryResult<UsageSummaryResponse>;
  readonly cnyPerUsd: number | null;
}): JSX.Element {
  const t = useT();
  const summary = props.usageQ.data;
  return (
    <KpiCard
      testId="dashboard-kpi-month-spend"
      label={t('dashboard.kpi.month_spend.label')}
      value={
        summary !== undefined
          ? formatUsdMicrosOrCentsAsCnyDisplay(
              summary.totalCostUsdMicros,
              summary.totalCostUsdCents,
              props.cnyPerUsd,
            )
          : '—'
      }
      delta={
        props.usageQ.isError
          ? {
              text: (
                <RetryInline
                  label={t('dashboard.kpi.unavailable')}
                  onRetry={() => {
                    void props.usageQ.refetch();
                  }}
                />
              ),
              tone: 'bad',
            }
          : summary !== undefined
            ? {
                text: t('dashboard.kpi.month_spend.hint', {
                  tokens: formatTokens(summary.totalInputTokens + summary.totalOutputTokens),
                }),
                tone: 'neutral',
              }
            : undefined
      }
    />
  );
}

function ActiveKeysKpi(props: {
  readonly keysQ: UseQueryResult<ListApiKeysResponse>;
}): JSX.Element {
  const t = useT();
  const keys = props.keysQ.data?.keys;
  const activeCount = keys?.filter((key) => key.revokedAt === null).length;
  return (
    <KpiCard
      testId="dashboard-kpi-active-keys"
      label={t('dashboard.kpi.active_keys.label')}
      value={activeCount !== undefined ? formatTokens(activeCount) : '—'}
      delta={
        props.keysQ.isError
          ? {
              text: (
                <RetryInline
                  label={t('dashboard.kpi.unavailable')}
                  onRetry={() => {
                    void props.keysQ.refetch();
                  }}
                />
              ),
              tone: 'bad',
            }
          : {
              text: (
                <Link
                  to="/access/keys"
                  className="text-signal transition-colors duration-120 hover:text-signal-bright"
                  data-testid="dashboard-kpi-active-keys-link"
                >
                  {t('dashboard.kpi.active_keys.hint')}
                </Link>
              ),
              tone: 'neutral',
            }
      }
    />
  );
}

function LastRequestKpi(props: {
  readonly hourlyQ: UseQueryResult<UsageHourlyResponse>;
}): JSX.Element {
  const t = useT();
  const buckets = props.hourlyQ.data?.buckets;
  const last = buckets !== undefined ? lastNonEmptyBucket(buckets) : undefined;
  return (
    <KpiCard
      testId="dashboard-kpi-last-request"
      label={t('dashboard.kpi.last_request.label')}
      value={
        last === undefined
          ? '—'
          : last === null
            ? t('dashboard.kpi.last_request.empty')
            : t('dashboard.kpi.last_request.value', { time: shortTimestamp(last.hour) })
      }
      delta={
        props.hourlyQ.isError
          ? {
              text: (
                <RetryInline
                  label={t('dashboard.kpi.unavailable')}
                  onRetry={() => {
                    void props.hourlyQ.refetch();
                  }}
                />
              ),
              tone: 'bad',
            }
          : { text: t('dashboard.kpi.last_request.hint'), tone: 'neutral' }
      }
    />
  );
}

function StatusStrip(props: {
  readonly statusQ: UseQueryResult<PublicStatusResponse>;
}): JSX.Element {
  const t = useT();
  const status = props.statusQ.data;
  return (
    <Panel
      caption={t('dashboard.status.caption')}
      padded
      testId="dashboard-status"
      actions={
        <Link
          to="/access/status"
          className={buttonClass('ghost', 'sm')}
          data-testid="dashboard-status-link"
        >
          {t('dashboard.status.view')}
        </Link>
      }
    >
      {props.statusQ.isLoading ? (
        <Skeleton className="h-8" testId="dashboard-status-loading" />
      ) : props.statusQ.isError ? (
        <PanelRetry
          message={t('dashboard.status.error')}
          onRetry={() => {
            void props.statusQ.refetch();
          }}
          testId="dashboard-status-error"
        />
      ) : status !== undefined ? (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          {/* The ONE live element on this page — pulse is reserved for it. */}
          <span
            className="inline-flex items-center gap-2 text-body font-medium text-paper"
            data-testid="dashboard-status-overall"
          >
            <StatusLamp
              tone={OVERALL_LAMP_TONE[status.summary.state]}
              pulse
              testId="dashboard-status-lamp"
            />
            {overallStateText(status.summary.state, t)}
          </span>
          {/* #1112: namespace chips when the SKU-marketplace rollup is
              present; vendor chips remain the pre-#906 fallback (#916). */}
          {status.namespaces !== undefined && status.namespaces.length > 0
            ? status.namespaces.map((namespace) => (
                <Chip
                  key={namespace.namespace_id}
                  tone={VENDOR_CHIP_TONE[namespace.state ?? 'healthy']}
                  testId={`dashboard-status-namespace-${namespace.namespace_slug}`}
                >
                  {namespace.namespace_display_name}
                </Chip>
              ))
            : status.vendors.map((vendor) => (
                <Chip
                  key={vendor.vendor}
                  tone={VENDOR_CHIP_TONE[vendor.state]}
                  testId={`dashboard-status-vendor-${vendor.vendor}`}
                >
                  {vendor.vendor_display_name}
                </Chip>
              ))}
          <span className="font-mono text-micro text-paper-low sm:ml-auto">
            {t('dashboard.status.updated', {
              timestamp: formatTimestamp(status.updated_at),
            })}
          </span>
        </div>
      ) : null}
    </Panel>
  );
}

function QuickActions(props: { readonly canManageBilling: boolean }): JSX.Element {
  const t = useT();
  return (
    <Panel caption={t('dashboard.quick.caption')} padded testId="dashboard-quick-actions">
      <div className="flex flex-wrap gap-2">
        <Link
          to="/access/keys"
          className={buttonClass('secondary')}
          data-testid="dashboard-action-create-key"
        >
          {t('dashboard.quick.create_key')}
        </Link>
        {props.canManageBilling ? (
          <Link
            to="/billing/topup"
            className={buttonClass('secondary')}
            data-testid="dashboard-action-top-up"
          >
            {t('dashboard.quick.topup')}
          </Link>
        ) : null}
        <Link
          to="/requests"
          className={buttonClass('secondary')}
          data-testid="dashboard-action-requests"
        >
          {t('dashboard.quick.requests')}
        </Link>
        <Link
          to="/billing/pricing"
          className={buttonClass('secondary')}
          data-testid="dashboard-action-pricing"
        >
          {t('dashboard.quick.pricing')}
        </Link>
      </div>
    </Panel>
  );
}

function UsageLink({ testId }: { readonly testId: string }): JSX.Element {
  const t = useT();
  return (
    <Link to="/usage" className={buttonClass('ghost', 'sm')} data-testid={testId}>
      {t('dashboard.trend.view_usage')}
    </Link>
  );
}

function TrendRow(props: {
  readonly hourlyQ: UseQueryResult<UsageHourlyResponse>;
  readonly dailyQ: UseQueryResult<UsageDailyResponse>;
  readonly cnyPerUsd: number | null;
}): JSX.Element {
  const t = useT();
  const hourlyBuckets = props.hourlyQ.data?.buckets;
  const requestDays = useMemo(() => rollupRequestsByDay(hourlyBuckets ?? []), [hourlyBuckets]);
  const spendDays = props.dailyQ.data?.days ?? [];

  return (
    <div className="grid grid-cols-1 gap-4 xl:grid-cols-2" data-testid="dashboard-trends">
      <Panel
        caption={t('dashboard.trend.requests.caption')}
        padded
        testId="dashboard-trend-requests"
        actions={<UsageLink testId="dashboard-trend-requests-link" />}
      >
        {props.hourlyQ.isLoading ? (
          <Skeleton className="h-24" />
        ) : props.hourlyQ.isError ? (
          <PanelRetry
            message={t('dashboard.trend.error')}
            onRetry={() => {
              void props.hourlyQ.refetch();
            }}
          />
        ) : requestDays.length === 0 ? (
          <p className="m-0 text-dense text-paper-low">{t('dashboard.trend.empty')}</p>
        ) : (
          <Line
            points={requestDays.map((day) => day.requests)}
            labels={requestDays.map((day) => `${day.date} · ${formatTokens(day.requests)}`)}
            ariaLabel={t('dashboard.trend.requests.caption')}
            testId="dashboard-trend-requests-chart"
          />
        )}
      </Panel>
      <Panel
        caption={t('dashboard.trend.spend.caption')}
        padded
        testId="dashboard-trend-spend"
        actions={<UsageLink testId="dashboard-trend-spend-link" />}
      >
        {props.dailyQ.isLoading ? (
          <Skeleton className="h-24" />
        ) : props.dailyQ.isError ? (
          <PanelRetry
            message={t('dashboard.trend.error')}
            onRetry={() => {
              void props.dailyQ.refetch();
            }}
          />
        ) : spendDays.length === 0 ? (
          <p className="m-0 text-dense text-paper-low">{t('dashboard.trend.empty')}</p>
        ) : (
          <Line
            points={spendDays.map((day) =>
              usdMicrosFromCentsFallback(day.costUsdMicros, day.costUsdCents),
            )}
            labels={spendDays.map(
              (day) =>
                `${day.date} · ${formatUsdMicrosOrCentsAsCnyMetered(
                  day.costUsdMicros,
                  day.costUsdCents,
                  props.cnyPerUsd,
                )}`,
            )}
            ariaLabel={t('dashboard.trend.spend.caption')}
            testId="dashboard-trend-spend-chart"
          />
        )}
      </Panel>
    </div>
  );
}

function RecentActivityPanel(props: {
  readonly hourlyQ: UseQueryResult<UsageHourlyResponse>;
  readonly cnyPerUsd: number | null;
}): JSX.Element {
  const t = useT();
  const buckets = (props.hourlyQ.data?.buckets ?? []).slice(-10).reverse();
  return (
    <Panel
      caption={t('dashboard.activity.title')}
      testId="dashboard-activity"
      actions={
        <Link
          to="/usage"
          className={buttonClass('ghost', 'sm')}
          data-testid="dashboard-activity-link"
        >
          {t('dashboard.activity.view_all')}
        </Link>
      }
    >
      {props.hourlyQ.isLoading ? (
        <div className="p-4">
          <TableSkeleton rows={5} testId="dashboard-activity-loading" />
        </div>
      ) : props.hourlyQ.isError ? (
        <div className="p-4">
          <PanelRetry
            message={t('dashboard.activity.error')}
            onRetry={() => {
              void props.hourlyQ.refetch();
            }}
            testId="dashboard-activity-error"
          />
        </div>
      ) : buckets.length === 0 ? (
        <EmptyState
          testId="dashboard-activity-empty"
          title={t('dashboard.activity.empty')}
          hint={t('dashboard.activity.empty_hint')}
          action={
            <Link to="/access/keys" className={buttonClass('secondary')}>
              {t('dashboard.quick.create_key')}
            </Link>
          }
        />
      ) : (
        <Table className="rounded-none border-0" testId="dashboard-activity-table">
          <THead>
            <Tr interactive={false}>
              <Th>{t('dashboard.activity.column.time')}</Th>
              <Th num>{t('dashboard.activity.column.requests')}</Th>
              <Th num>{t('dashboard.activity.column.tokens')}</Th>
              <Th num>{t('dashboard.activity.column.cost')}</Th>
            </Tr>
          </THead>
          <TBody>
            {buckets.map((bucket) => (
              <Tr key={bucket.hour} data-testid={`dashboard-activity-row-${bucket.hour}`}>
                <Td mono heading>
                  {formatTimestamp(bucket.hour)}
                </Td>
                <Td num emphasis label={t('dashboard.activity.column.requests')}>
                  {formatTokens(bucket.requests)}
                </Td>
                <Td num label={t('dashboard.activity.column.tokens')}>
                  {formatTokens(bucket.inputTokens + bucket.outputTokens)}
                </Td>
                <Td num label={t('dashboard.activity.column.cost')}>
                  {formatUsdMicrosOrCentsAsCnyMetered(
                    bucket.costUsdMicros,
                    bucket.costUsdCents,
                    props.cnyPerUsd,
                  )}
                </Td>
              </Tr>
            ))}
          </TBody>
        </Table>
      )}
    </Panel>
  );
}

function keyDisplayName(
  name: string | null,
  prefix: string | null,
  t: (key: I18nKey, vars?: Record<string, string | number>) => string,
): string {
  if (typeof name === 'string' && name.length > 0) return name;
  if (typeof prefix === 'string' && prefix.length > 0) return prefix;
  return t('usage.by_key.unnamed_key');
}

interface TopKeyAggregate {
  readonly apiKeyId: string;
  readonly apiKeyName: string | null;
  readonly apiKeyPrefix: string | null;
  readonly requests: number;
  readonly costUsdMicros: number;
}

/**
 * `/v1/usage/by-key` rows are one per key×SKU since epic #898 P6
 * (#913); the "top keys" panel keeps its per-KEY semantics by folding
 * the rows back down to distinct `apiKeyId` before ranking by spend.
 */
function aggregateTopKeys(
  rows: readonly UsageByKeyResponse['keys'][number][],
): readonly TopKeyAggregate[] {
  const byKey = new Map<string, TopKeyAggregate>();
  for (const row of rows) {
    const existing = byKey.get(row.apiKeyId);
    byKey.set(row.apiKeyId, {
      apiKeyId: row.apiKeyId,
      apiKeyName: existing?.apiKeyName ?? row.apiKeyName,
      apiKeyPrefix: existing?.apiKeyPrefix ?? row.apiKeyPrefix,
      requests: (existing?.requests ?? 0) + row.requests,
      costUsdMicros:
        (existing?.costUsdMicros ?? 0) +
        usdMicrosFromCentsFallback(row.costUsdMicros, row.costUsdCents),
    });
  }
  return [...byKey.values()].sort((a, b) => b.costUsdMicros - a.costUsdMicros);
}

function TopKeysPanel(props: {
  readonly byKeyQ: UseQueryResult<UsageByKeyResponse>;
  readonly cnyPerUsd: number | null;
}): JSX.Element {
  const t = useT();
  const entries = aggregateTopKeys(props.byKeyQ.data?.keys ?? []).slice(0, 5);
  return (
    <Panel
      caption={t('dashboard.top_keys.title')}
      testId="dashboard-top-keys"
      actions={
        <Link
          to="/usage"
          className={buttonClass('ghost', 'sm')}
          data-testid="dashboard-top-keys-link"
        >
          {t('dashboard.top_keys.view_usage')}
        </Link>
      }
    >
      {props.byKeyQ.isLoading ? (
        <div className="p-4">
          <TableSkeleton rows={3} testId="dashboard-top-keys-loading" />
        </div>
      ) : props.byKeyQ.isError ? (
        <div className="p-4">
          <PanelRetry
            message={t('dashboard.top_keys.error')}
            onRetry={() => {
              void props.byKeyQ.refetch();
            }}
            testId="dashboard-top-keys-error"
          />
        </div>
      ) : entries.length === 0 ? (
        <EmptyState
          testId="dashboard-top-keys-empty"
          title={t('dashboard.top_keys.empty')}
          hint={t('dashboard.top_keys.empty_hint')}
          action={
            <Link to="/access/keys" className={buttonClass('secondary')}>
              {t('dashboard.quick.create_key')}
            </Link>
          }
        />
      ) : (
        <Table className="rounded-none border-0" testId="dashboard-top-keys-table">
          <THead>
            <Tr interactive={false}>
              <Th>{t('dashboard.top_keys.column.key')}</Th>
              <Th num>{t('dashboard.top_keys.column.requests')}</Th>
              <Th num>{t('dashboard.top_keys.column.cost')}</Th>
            </Tr>
          </THead>
          <TBody>
            {entries.map((entry) => (
              <Tr key={entry.apiKeyId} data-testid={`dashboard-top-key-${entry.apiKeyId}`}>
                <Td mono emphasis heading>
                  {keyDisplayName(entry.apiKeyName, entry.apiKeyPrefix, t)}
                </Td>
                <Td num label={t('dashboard.top_keys.column.requests')}>
                  {formatTokens(entry.requests)}
                </Td>
                <Td num label={t('dashboard.top_keys.column.cost')}>
                  {formatUsdMicrosOrCentsAsCnyMetered(entry.costUsdMicros, 0, props.cnyPerUsd)}
                </Td>
              </Tr>
            ))}
          </TBody>
        </Table>
      )}
    </Panel>
  );
}
